import { EXCEEDANCE_STAGE_LABELS } from "encamp-shared/src/constants/generatorDiscrepancyDefaults";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ResolutionStatus } from "generated-graphql/graphql";

/**
 * Lifecycle stage labels for the top-bar Status filter and the grid's Status chip
 * copy. The vocabulary lives in encamp-shared (generatorDiscrepancyDefaults) —
 * the same constants the server's `stage` omnisearch filter/sort SQL and the
 * staff CSV export interpolate — so no surface can rename a stage alone.
 */
export const STAGE_LABELS: Record<string, string> = EXCEEDANCE_STAGE_LABELS;

export const STAGE_ENUM_VALUES = Object.keys(STAGE_LABELS);

/**
 * Default visible stages — everything except Resolved, matching the server's
 * default (it auto-hides terminal RESOLVED rows unless the caller filters by
 * stage). IGNORED stays visible deliberately so a dismissed false-positive never
 * silently vanishes.
 */
export const DEFAULT_STAGES = STAGE_ENUM_VALUES.filter((stage) => stage !== "RESOLVED");

/**
 * Stage label shown on the status chips (grid + drawer) — richer than the bare
 * enum where it helps: the two UNRESOLVED sub-states read as their queue
 * position, a PENDING row reads as its in-flight submission's status, and a row
 * parked on manifest corrections says so.
 */
export function stageLabel(row: {
  resolutionStatus: ResolutionStatus;
  validatedAt?: string | null;
  // Loose `string` on purpose: the grid passes the GraphQL SubmissionStatus enum
  // while the drawer's summary type carries a plain string; getStatusLabel
  // accepts either.
  linkedSubmission?: { status: string } | null;
  openSubmission?: { status: string } | null;
  manifestCorrectionCount?: number | null;
}): string {
  if (row.resolutionStatus === ResolutionStatus.Unresolved) {
    return row.validatedAt
      ? EXCEEDANCE_STAGE_LABELS.READY_FOR_PICKUP
      : EXCEEDANCE_STAGE_LABELS.AWAITING_VALIDATION;
  }
  if (row.resolutionStatus === ResolutionStatus.Pending) {
    // Ordered to match the server's `stage` CASE: a linked submission wins, then
    // cited corrections, then the bare fallback. Only the LINKED submission counts
    // here — the facility's open notification isn't what this row is waiting on.
    if (!row.linkedSubmission && (row.manifestCorrectionCount ?? 0) > 0) {
      return EXCEEDANCE_STAGE_LABELS.AWAITING_CORRECTION;
    }
    const sub = row.linkedSubmission ?? row.openSubmission;
    return sub ? getStatusLabel(sub.status) : "Pending";
  }
  return prettyPrintEnumValue(row.resolutionStatus);
}
